/** Suntronix ONE — Bundle Detail page.
 *  Route: /services/bundle/:code
 *  Shows the bundle description, every included service, default add-ons and
 *  a single "Start bundle" CTA. */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { rollupSLA } from "@/lib/slaRollup";
import { ArrowLeft, Layers, Zap, ChevronRight, Sparkles, Star, Clock } from "lucide-react";

function pricing(s) {
  const v = s.pricing_visibility || "show_price";
  if (v === "quotation_required") return "Quotation on request";
  if (v === "contact_suntronix") return "Contact Suntronix";
  if (v === "included_in_contract") return "Included in contract";
  if (v === "hide" || s.default_price == null) return null;
  const cur = s.default_currency || "INR";
  try {
    const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(s.default_price);
    return `${money} · ${(s.billing_method || "per_device").replace(/_/g, " ")}`;
  } catch { return `${cur} ${s.default_price}`; }
}

export default function ServiceBundle() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/bundle/${code}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Bundle not found"))
      .finally(() => setLoading(false));
  }, [code]);

  const slaRollup = useMemo(() => rollupSLA(data?.services), [data?.services]);

  if (loading) return <div className="max-w-4xl space-y-4"><div className="st-skeleton h-8 w-64" /><div className="st-skeleton h-32" /><div className="st-skeleton h-40" /></div>;
  if (error) return (
    <div className="max-w-3xl space-y-4">
      <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" /> Back to services</Link>
      <div className="bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-700">{error}</div>
    </div>
  );

  const b = data.bundle;
  return (
    <div className="max-w-5xl space-y-6">
      <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="bundle-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to services
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[color:var(--st-accent)] bg-[color:var(--st-accent-soft)] rounded-full px-2 py-0.5">
                <Layers className="h-3 w-3" /> Bundle
              </span>
              <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">{(b.group || "").replace(/_/g, " ")}</div>
              {b.is_featured && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-700 bg-slate-100 rounded-full px-2 py-0.5">
                  <Star className="h-3 w-3" /> Featured
                </span>
              )}
            </div>
            <h1 className="mt-1 font-heading text-[26px] font-semibold text-slate-900 tracking-tight">{b.customer_facing_name || b.name}</h1>
            {b.description && <p className="mt-2 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{b.description}</p>}
            <div className="mt-3 text-[11px] text-slate-500">Includes {data.services.length} service{data.services.length === 1 ? "" : "s"}{data.addons?.length ? ` and ${data.addons.length} default add-on${data.addons.length === 1 ? "" : "s"}` : ""}.</div>
            {slaRollup && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-700" data-testid="bundle-sla-rollup">
                <Clock className="h-3.5 w-3.5 text-slate-500" strokeWidth={1.6} />
                Bundle SLA: <span className="font-medium">{slaRollup.label}</span>
                <span className="text-slate-400">· longest of {slaRollup.count} service{slaRollup.count === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/services/request/${b.code}?type=bundle`)}
            className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 inline-flex items-center gap-2 shrink-0"
            data-testid="bundle-start"
          >
            <Zap className="h-4 w-4" strokeWidth={1.6} /> Start bundle
          </button>
        </div>
      </div>

      {/* Included services */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-100 text-xs font-semibold tracking-wide uppercase text-slate-500">Included services</div>
        <ul className="divide-y divide-slate-100">
          {data.services.map((s, i) => {
            const p = pricing(s);
            return (
              <li key={s.code}>
                <Link to={`/services/detail/${s.code}`} className="flex items-start justify-between gap-3 px-5 py-4 hover:bg-slate-50" data-testid={`bundle-svc-${s.code}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-medium text-slate-900">{s.customer_facing_name || s.name}</span>
                      {s.is_premium && <Sparkles className="h-3 w-3 text-[color:var(--st-accent)]" />}
                    </div>
                    {s.short_description && <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.short_description}</div>}
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                      {s.sla_target && <span>SLA {s.sla_target}</span>}
                      {p && <span className="font-mono text-slate-700">{p}</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Add-ons */}
      {data.addons?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">Default add-ons</div>
          <ul className="space-y-2">
            {data.addons.map((a) => (
              <li key={a.code} className="text-sm" data-testid={`bundle-addon-${a.code}`}>
                <div className="font-medium text-slate-800">{a.name}</div>
                {a.description && <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{a.description}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
